import grapesjs, { Component, Editor } from "grapesjs";
import { JSONPath } from "jsonpath-plus";
import { gjsPlugin } from "./gjsplugin";
import { GjsActionTypes, GjsAttributeNames, GjsComponentTypes, GjsMetaTypes } from "./gjsplugin/Enums";
import Utils from "../../classes/Utilities";
import AssetUtility from "../../classes/AssetUtility";
import {
	CssProperties,
	CssStates,
	GrapesRenderModes,
	IDisplayDeviceInfo,
	IGrapesJSComponent,
	IGrapesJSProjectData,
	IGrapesJSStyle,
	IVisageTemplate,
	VisageEditorWindowModes,
	defaultDevice,
	ICssPropertyRenderQuery,
	CssPositionTypes,
	ICssNumericValue,
	CssUnits,
	IAttributePropertyRenderQuery,
	VisageStyleStates,
	VisageComponentTypes,
	IVisageComponentDefinition,
	IVisageComponentStyle,
	VisageContextStylesMap,
	nativeHtmlEventAttributes,
	SmartIdentifierStates
} from "../../interfaces/VisageUI";
import { assert } from "../../classes/utilities/ErrorHandling";
import store, { StoreActions } from "../../state/store";
import Scenario from "../../classes/scenarioElements/Scenario";
import { IScenarioPickerInfo } from "../../interfaces/ScenarioElements";
import ProjectPacker from '../../classes/ProjectPacker';
import { enumFromString, getUuidv4, isTangible, JsonCopy } from "../../classes/utilities/Misc";
import TextVariables from "../../classes/TextVariables";
import { IActionValuesChange, IDropdownOption } from "../../interfaces/Misc";
import SetGrapesProjectAction from "../../actions/visageUI/SetGrapesProjectAction";
import { ContentAnchors } from "../../globals";
import TextUtils from "../../classes/utilities/TextUtils";

/** **WARNING!! THIS OBJECT IS FOR UNIT TEST PURPOSES ONLY! NEVER IMPORT THIS IN OTHER SOURCE FILES! WARNING!!** */
export const WARNING_EXPORTED_FOR_TESTING_ONLY = {
	extractProjectStylesIntoMap: extractProjectStylesIntoMap,
	mergeCssTransformProperty: mergeCssTransformProperty,
	mergeClassStyleIntoComponents: mergeClassStyleIntoComponents,
	populateComponentStylesIntoProject: populateComponentStylesIntoProject,
}

export interface ICreateGrapesEditorParams
{
	mainContainerElement?: HTMLDivElement,
	layersContainerElement?: HTMLDivElement,
	blocksContainerElement?: HTMLDivElement,
	device?: IDisplayDeviceInfo,
	dragMode?: "absolute" | "translate" | undefined,
	onUpdate?: (editor: Editor) => void,
}

export function createGrapesEditor(params: ICreateGrapesEditorParams): Editor
{
	const editor = grapesjs.init({
		container: params.mainContainerElement ?? createHiddenGrapesEditorContainer(),
		storageManager: false,
		nativeDnD: false,
		dragMode: params.dragMode, // enable this to disable automatic layout and instead use only absolute positioning when dragging with the gizmo
		width: (params.device?.resolution.width ?? 0) + "px",
		height: (params.device?.resolution.height ?? 0) + "px",
		plugins: [
			gjsPlugin,
		],
		assetManager: {}, // disable the default asset manager
		layerManager: !params.layersContainerElement ? {} : {
			appendTo: params.layersContainerElement,
		},
		blockManager: !params.blocksContainerElement ? {} : {
			appendTo: params.blocksContainerElement,
		},
		deviceManager: {
			devices: [
				{ 
					name: params.device?.name ?? "",
					width: `${params.device?.resolution.width}px`,
					height: `${params.device?.resolution.height}px`,
					widthMedia: ``, // empty prevents @media being created
				},
			],
		},
		panels: {
			defaults: [],
		},
		showDevices: false,
	});
	
	// fix for background-image property in the style manager
	editor.on('component:styleUpdate:background-image', function(component)
	{
		const changedStyle = component?.rule?.changed?.style;
		if(changedStyle)
		{
			const backgroundImage = changedStyle['background-image'];

			// thanks https://stackoverflow.com/a/901144/618726
			const urlParams = Object.fromEntries(new URLSearchParams(backgroundImage ?? ""));
			const assetId = urlParams['asset'];
			const attributes = component.get('attributes');
			if(assetId)
			{
				if(attributes[GjsAttributeNames.CoverAssetID])
				{
					component.set('attributes', { ...attributes, [GjsAttributeNames.CoverAssetID]: assetId });
				}
				else
				{
					component.set('attributes', { ...attributes, [GjsAttributeNames.AssetID]: assetId });
				}
			}
			else
			{
				if(attributes[GjsAttributeNames.CoverAssetID])
				{
					component.set('attributes', { ...attributes, [GjsAttributeNames.CoverAssetID]: "" });
				}
				else
				{
					component.set('attributes', { ...attributes, [GjsAttributeNames.AssetID]: "" });
				}
			}
		}
	});
	
	return editor;
}

export function fixGrapesPreviewUrls(data: IGrapesJSProjectData): void
{
	traverseGrapesProject(data, ({component, projectData}) => 
	{
		if(component?.attributes)
		{
			const assetId = component.attributes[GjsAttributeNames.AssetID];
			if(assetId)
			{
				const previewUrl = AssetUtility.getPreviewAssetURL(assetId);
				if(previewUrl)
				{
					if(component.attributes.src) // img tags
					{
						component.attributes.src = previewUrl;
					}
					else // background-image css
					{
						const componentStyles = getStylesForComponent(projectData as IGrapesJSProjectData, component.attributes.id);
						for(const style of componentStyles)
						{
							if(style.style["background-image"])
							{
								style.style["background-image"] = `url(${previewUrl})`;
							}
						}
					}
				}
			}
		}
	});
}

/**
 * Goes over all components in the given project and adds an ID to each component if it doesn't have one
 * 
 * **Important:** This function modifies the project data in-place, so make sure to pass in a writable object!
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 */
export function fixGrapesComponentIDs(grapesProject: IGrapesJSProjectData): void
{
	traverseGrapesProject(grapesProject, ({component}) => 
	{
		if(!component.attributes)
		{
			component.attributes = 
			{
				id: "C" + getUuidv4(),
			};
		}
		else if (!component.attributes.id)
		{
			component.attributes.id = "C" + getUuidv4();
		}
	});
}

export function synchronizeTemplateToSteps(templateId: string): void
{
	assert(store.getState().visageModule.uiTemplateData[templateId], `Template data is undefined for template ${templateId} Unable to synchronize template to steps.`);
	if(!store.getState().visageModule.uiTemplateData[templateId]) throw ReferenceError(`no data for template ${templateId}`); // assert above should throw but we need to break for mocked tests and ts
	const template = JsonCopy(store.getState().visageModule.uiTemplateData[templateId]) as IVisageTemplate;

	const stepTemplateMap = store.getState().visageModule.stepTemplateMap;
	for(const [stepId, assignedTemplateId] of Object.entries<string>(stepTemplateMap))
	{
		// step does not use the same template; skip
		if(assignedTemplateId !== templateId) continue;

		assert(store.getState().visageModule.stepUIData[stepId], `Missing step UI data for step ${stepId} even though it has a template assigned`);
		if(!store.getState().visageModule.stepUIData[stepId]) throw ReferenceError(`no ui data for step ${stepId}`); // assert above should throw but we need to break for mocked tests and ts
		const stepUIData = JsonCopy(store.getState().visageModule.stepUIData[stepId]) as IGrapesJSProjectData;

		// TODO: we need to handle deleted things from the tempalte that still exist in the step, both styles and components
		// after we go over the template stuff, we need to go over the step stuff, and if it doesn't exist in current but does exist in previous then we need to delete from the step
		synchronizeTemplateStylesToStep(template, stepUIData);
		synchronizeTemplateComponentsToStep(template, stepUIData);

		// update step data in redux
		store.dispatch(StoreActions.visage.setStepUIData({ stepId: stepId, data: JsonCopy(stepUIData) }));
	}
}

/**
 * synchronizes the styles from a template to a step  
 * it does so by adding, deleting, and updating styles as needed, while skipping changes that were made by the user to the step  
 * in general, if the step data differs from the previous state of the template, it means the step has modifications that should be skipped  
 * 
 * we check for each property the following:  
 * 1. if the style property does not exist on the step but DOES exist on the previous template state, we assume it was deleted deliberately and we skip it  
 * 2. if the style property does not exist on both the step and the previous template state, we add it to the step as it's a new addition to the template  
 * 3. if the style property exists on both the step and the previous tempalte state, we compare them to see if they are different:  
 * 3.1. if they are different, it means the step modified it and we skip it  
 * 3.2. if they are the same, it means the step did not modify it and we update it to the current state  
 * 
 * @export
 * @param {IVisageTemplate} template
 * @param {IGrapesJSProjectData} stepUIData
 */
export function synchronizeTemplateStylesToStep(template: IVisageTemplate, stepUIData: IGrapesJSProjectData): void
{
	// TODO: optimize this thing somehow. used to be mapx, maybe we can do it again? at least partially for the template styles? 
	// in any case, we should keep in mind it's a possible slowdown point for templates / steps with many styles
	const currentTemplateStyles = template.content.styles;
	const previousTemplateStyles = template.previousState ? template.previousState.styles : currentTemplateStyles; // if no previous state, use the current state so it'll always be different and we'll always update
	const stepStyles = stepUIData.styles;

	function matchSelectorAndState(styleA: IGrapesJSStyle, styleB: IGrapesJSStyle): boolean
	{
		return 	styleA.selectors.join(",") === styleB.selectors.join(",") && 
				styleA.selectorsAdd === styleB.selectorsAdd && 
				styleA.state === styleB.state;
	}

	for(const currentTemplateStyle of currentTemplateStyles)
	{
		const stepStyle = stepStyles.find((stepStyle) => matchSelectorAndState(stepStyle, currentTemplateStyle));
		if(!stepStyle)
		{
			// not in step, so we add it
			stepStyles.push(currentTemplateStyle);
		}
		else
		{
			// store the previoius style (or default to current style)
			const previousStyle = previousTemplateStyles.find((previousTemplateStyle) => matchSelectorAndState(previousTemplateStyle, currentTemplateStyle)) || currentTemplateStyle;

			// in step, so we compare each style property from the current template to the step style
			for(const [key, value] of Object.entries(currentTemplateStyle.style))
			{
				// the property is missing from the step, so we need to check if it was deliberately deleted and add it if not
				if(!stepStyle.style[key])
				{
					// if the property exists in the previous state, it means it was deleted deliberately and we skip
					if(previousStyle.style[key]) continue;

					// otherwise we add it to the step
					stepStyle.style[key] = value;
				}
				// the property exists on the step, so we compare it against the template state to see if it was manually modified by the user
				else
				{
					// if the property doesn't exist on the previous state for some reason we add it to the step
					if(!previousStyle.style[key])
					{
						stepStyle.style[key] = value;
					}
					// if the step value differs from the previous tempalte value, it was modified by the user and we skip
					else if(stepStyle.style[key] !== previousStyle.style[key])
					{
						// Nothing
					}
					// the step value is the same as the previous state, so we update to the new state
					else
					{
						stepStyle.style[key] = value;
					}
				}
			}
		}
	}
}

export function synchronizeTemplateComponentsToStep(template: IVisageTemplate, stepUIData: IGrapesJSProjectData): void
{
	function generateComponentsMap(projectData: IGrapesJSProjectData): Map<string, { component: IGrapesJSComponent, parentId: string }>
	{
		const output = new Map<string, { component: IGrapesJSComponent, parentId: string }>();
		
		function traverseComponent(component: IGrapesJSComponent)
		{
			if(component.components)
			{
				for(const subComponent of component.components)
				{
					assert(subComponent?.attributes?.id, "Component has no id");
					output.set(subComponent.attributes.id, { component: subComponent, parentId: component.attributes.id });
					traverseComponent(subComponent);
				}
			}
		}
	
		if(!projectData || !projectData.pages) return output;
		for(const page of projectData.pages)
		{
			if(!page.frames) continue;
			for(const frame of page.frames)
			{
				if(!frame.component) continue;
				const frameComponent = frame.component;
	
				if(!frameComponent.components) continue;
				for(const component of frameComponent.components)
				{
					output.set(component.attributes.id, { component: component, parentId: "" });
					traverseComponent(component);
				}
			}
		}

		return output;
	}

	// need a reference to the step's root component so we can add stuff to it if needed
	const stepRootComponent = stepUIData?.pages?.at(0)?.frames?.at(0)?.component;

	// in case the step root component is missing, it means the step has no grapes data, so we can just assign to it the entire template and be done with it
	if(!stepRootComponent)
	{
		stepUIData = JsonCopy(template.content);
		return;
	}

	// we need to have a reference for each step component in order to update it correctly
	const currentComponentsMap = generateComponentsMap(template.content);

	if(!template.previousState) 
	{
		stepUIData = JsonCopy(template.content);
		return;
	}
	const previousComponentsMap = generateComponentsMap(template.previousState);
	let stepComponentMap = generateComponentsMap(stepUIData);

	// additions and synchronization
	for(const [componentId, componentInfo] of currentComponentsMap.entries())
	{
		// if the component exits in the step, synchronize it
		if(stepComponentMap.has(componentId))
		{
			const currentComponent = currentComponentsMap.get(componentId)?.component;
			assert(currentComponent, "Current component is undefined even though it exists in the map");
			if(!currentComponent) throw ReferenceError("current component is undefined"); // assert above should throw but we need to break for mocked tests and ts

			const stepComponent = stepComponentMap.get(componentId)?.component;
			assert(stepComponent, "Step component is undefined even though it exists in the map");
			if(!stepComponent) throw ReferenceError("step component is undefined"); // assert above should throw but we need to break for mocked tests and ts

			const previousComponent = previousComponentsMap.get(componentId)?.component; // this one can actually be undefined, since it might not exist in the previous state
			
			// set attributes
			for(const [key, value] of Object.entries(currentComponent.attributes))
			{
				// if there is a previous state and step differs from it, that means the user modified the step value and we skip
				if(previousComponent && previousComponent.attributes[key] !== stepComponent.attributes[key]) continue;

				// otherwise we set the value directly - we don't care about overwriting the same value
				stepComponent.attributes[key] = value;
			}

			// set content (for text components) - note that this isn't part of the "attributes" array of the component
			if(currentComponent.content)
			{
				// if there is a previous state and step differs from it, that means the user modified the step value and we skip
				if(previousComponent && previousComponent.content !== stepComponent.content) continue;

				// otherwise we set the value directly - we don't care about overwriting the same value
				stepComponent.content = currentComponent.content;
			}
		}
		else // component does not exist on the step
		{
			// if it exists in the previous state, it means it was deleted from the step and we skip
			if(previousComponentsMap.has(componentId)) continue;

			// otherwise we add it to the step under the correct parent, going up the hierarchy until we find the parent
			// find first parent component that exists, or if none exist, add it to the root
			let parentId = currentComponentsMap.get(componentId)!.parentId;
			let stepParentComponent: IGrapesJSComponent | undefined;

			// if we have to go up the hierarchy even further, we need to create all the components in between
			// but this means that the currently iterated component will be created as a by-product of its parent being created
			// so we need to keep track of the id of the component we want to create, and then create it after we're done with the loop
			let componentToCreateId: string = componentId; 

			while(parentId !== "")
			{
				if(stepComponentMap.has(parentId))
				{
					stepParentComponent = stepComponentMap.get(parentId)!.component;
					break;
				}

				// parent not found yet, so we want to mark it for creation first
				componentToCreateId = parentId;

				// and then we go up the hierarchy
				parentId = currentComponentsMap.get(parentId)!.parentId;
			}

			// if we found a parent, add the component to it
			if(stepParentComponent)
			{
				// components can be undefined for components that have no children in grapesjs
				if(!stepParentComponent.components)
				{
					stepParentComponent.components = [];
				}

				stepParentComponent.components.push(JsonCopy(currentComponentsMap.get(componentToCreateId)!.component));
			}
			// otherwise add it to the root
			else
			{
				if(!stepRootComponent.components)
				{
					stepRootComponent.components = [];
				}

				stepRootComponent.components.push(JsonCopy(currentComponentsMap.get(componentToCreateId)!.component));
			}

			// update the step component map to include the new component(s)
			stepComponentMap = generateComponentsMap(stepUIData);
		}
	}

	// handle deletions
	for(const componentId of stepComponentMap.keys())
	{
		// if the key exists in the step AND exists in the previoue state BUT NOT in the current state, it was deleted and needs to be removed from the step as well
		if(previousComponentsMap.has(componentId) && !currentComponentsMap.has(componentId))
		{
			// get the parent component
			const parentId = stepComponentMap.get(componentId)!.parentId;
			const parentComponent = parentId ? stepComponentMap.get(parentId)!.component : stepRootComponent;

			// remove the component from the parent
			parentComponent.components?.splice(parentComponent.components?.findIndex((c) => c.attributes.id === componentId), 1);
		}
	}

	// handle movement
	for(const [componentId, componentInfo] of currentComponentsMap.entries())
	{
		// ignore deleted components
		if(!stepComponentMap.has(componentId)) continue;

		// check parent
		if(componentInfo.parentId !== stepComponentMap.get(componentId)!.parentId)
		{
			// get the parent component
			const parentId = stepComponentMap.get(componentId)!.parentId;
			const parentComponent = parentId ? stepComponentMap.get(parentId)!.component : stepRootComponent;

			// remove the component from the parent
			parentComponent.components?.splice(parentComponent.components?.findIndex((c) => c.attributes.id === componentId), 1);

			// add the component to the new parent
			const newParentComponent = stepComponentMap.get(componentInfo.parentId)?.component ?? stepRootComponent;			
			if(!newParentComponent.components) 
			{
				newParentComponent.components = [];
			}
			newParentComponent.components.push(JsonCopy(componentInfo.component));
		}
	}
}

export const commonCSS = `
	* {
		-webkit-touch-callout: none;
		-webkit-user-select: none;
		-khtml-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		user-select: none;
		-webkit-user-drag: none;
	}

	body {
		overflow: hidden;
	}
`;

export const basicHTML = `
	<html>
		<head>
			<meta name="transparent" content="true">
   			<meta charset="utf-8">
			<style>
				${commonCSS}
			</style>
		</head>
		<body>
		</body>
	</html>
`;

export const commonJS = `
	function traverseDOM(element, callback)
	{
		callback(element);
		element.childNodes.forEach((child) => traverseDOM(child, callback));
	}
`;

export const defaultJSForPublish = `
	${commonJS}
	
	function sendMessageToUnity(message) 
	{
		if (!window.vuplex)
		{
			console.warn("Sending message to Unity but Vuplex is not ready yet; dismissing message", message);
			return;
		}

		window.vuplex.postMessage(message);
	}

	function getSetupElements()
	{
		/*
			traverse the dom and return an array of all elements that are required by the app for setup, in the following (stringified) format:
			[
				{ "type": "camera", "elementId": "idString", "cameraIndex": "cameraIndexNumber" },
				{ "type": "video" , "elementId": "idString", "assetPath": "videoAssetPathString.ext" },
				{ "type": "audio" , "elementId": "idString", "assetPath": "audioAssetPathString.ext" },
			]
		*/
		
		const output = [];
		const elements = document.querySelectorAll('div, canvas');
		for(const element of elements)
		{
			const elementId = element.getAttribute("${GjsAttributeNames.ID}");
			if(!elementId) continue;
		
			const metaType = element.getAttribute("${GjsAttributeNames.MetaType}");
			if(!metaType) continue;
		
			switch(metaType)
			{
				case "${GjsMetaTypes.Camera}":
					const cameraIndex = element.getAttribute("${GjsAttributeNames.ParamCameraID}");
					if(cameraIndex !== null && cameraIndex !== "")
					{
						const cameraZIndex = window.getComputedStyle(element).zIndex;
						output.push({ type: metaType, elementId: elementId, cameraIndex: cameraIndex, zIndex: cameraZIndex });
					}
					else
					{
						console.warn("Camera element has no camera index", element);
					}
					break;
				case "${GjsMetaTypes.Video}":
					const videoAssetPath = element.getAttribute("${GjsAttributeNames.AssetPath}");
					if(videoAssetPath !== null && videoAssetPath !== "")
					{
						output.push({ type: metaType, elementId: elementId, assetPath: videoAssetPath });
					}
					else
					{
						console.warn("Video element has no asset path", element);
					}
					break;
				case "${GjsMetaTypes.Audio}":
					const audioAssetPath = element.getAttribute("${GjsAttributeNames.AssetPath}");
					if(audioAssetPath !== null && audioAssetPath !== "")
					{
						output.push({ type: metaType, elementId: elementId, assetPath: audioAssetPath });
					}
					else
					{
						console.warn("Audio element has no asset path", element);
					}
					break;
				default:
					console.error("Element has unknown meta type", element);
					break;
			}
		}
			
		return JSON.stringify(output);
	}
`;

export const defaultJSForPreview = `
	${commonJS}

	function sendMessageToUnity(message)
	{	
		console.log("Sending message to Unity", message);		
		window.parent.postMessage({ type: "messageToUnity", detail: message }, "*");
	}
`;

function getDefaultJS(renderMode: GrapesRenderModes): string
{
	switch(renderMode)
	{
		case GrapesRenderModes.Preview:
			return defaultJSForPreview;
		case GrapesRenderModes.Publish:
		default: // fallback to publish
			return defaultJSForPublish;
	}
}

let grapesRenderer: Editor;
let grapesRendererContainer: HTMLDivElement;

function createHiddenGrapesEditorContainer()
{
	const container = document.createElement('div');
	container.id = 'grapes-renderer-container';
	container.style.display = 'none';

	return container;
}

export function createGrapesRenderer()
{
	if(!grapesRendererContainer)
	{
		grapesRendererContainer = createHiddenGrapesEditorContainer();
	}

	if(!grapesRenderer)
	{
		// grapesRenderer = grapesjs.init({
		// 	container: grapesRendererContainer,
		// 	storageManager: false,
		// });
		grapesRenderer = createGrapesEditor({
			mainContainerElement: grapesRendererContainer,
			device: defaultDevice(),
		});
	}
}

export function resetGrapesJSEditor(editor: Editor)
{
	editor.DomComponents.clear(); // Clear components
	editor.CssComposer.clear();  // Clear styles
	editor.UndoManager.clear(); // Clear undo history
	editor.setComponents([{}, {}]) // Clear components
	editor.setStyle([{}, {}]) // Clear rules
}

export function getAssetsFromGrapesJS(grapesProjectData: IGrapesJSProjectData): string[]
{
	//console.log("getAssetsFromGrapesJS", grapesProjectData);
	if(!grapesProjectData || !grapesProjectData.pages) return [];

	const output = new Set<string>();

	traverseGrapesProject(grapesProjectData, ({component}) =>
	{
		if(component.attributes)
		{
			output.add(component.attributes[GjsAttributeNames.AssetID]);
			output.add(component.attributes[GjsAttributeNames.CoverAssetID]);
		}
	});

	return Array.from(output).filter((assetId) => assetId !== undefined && assetId !== "");
}

/**
 * This function is used to get the connections from a GrapesJS project to a specific step.
 * It traverses the GrapesJS project and checks each component for specific actions that would lead to a connection to a step.
 * The function returns an array of step IDs that the GrapesJS project is connected to.
 *
 * @param {IGrapesJSProjectData} grapesProjectData - The GrapesJS project data to traverse.
 * @param {string} stepId - The ID of the step to check connections for.
 * @returns {string[]} - An array of step IDs that the GrapesJS project is connected to.
 */
export function getStepConnectionsFromGrapesJS(grapesProjectData: IGrapesJSProjectData, stepId: string): string[]
{
	// Initialize a set to store the connections
	const connections = new Set<string>();
	traverseGrapesProject(grapesProjectData, ({component}) =>
	{
		// Check if the component has attributes and an action
		if(component.attributes && component.attributes[GjsAttributeNames.Action])
		{
			// Get the action of the component
			const action = component.attributes[GjsAttributeNames.Action];
			switch(action)
			{
				// If the action is "GoToStep", add the step ID to the connections
				case GjsActionTypes.MoveToStep:
					const paramStepId = component.attributes[GjsAttributeNames.ParamStepID];
					if(paramStepId) connections.add(paramStepId);
					break;
					// If the action is "NextStep", add the next step ID to the connections
				case GjsActionTypes.NextStep:
					const nextStepId = Scenario.getStep(stepId)?.getNextStep()?.id;
					if(nextStepId) connections.add(nextStepId);
					break;
			}
		}
	});

	// Return the connections as an array
	return Array.from(connections);
}

/**
 * Checks the GrapesJS project for any component that is connected to the previous step
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProjectData
 * @param {string} stepId
 * @return {*}  {boolean}
 */
export function isConnectedToPreviousStep(grapesProjectData: IGrapesJSProjectData, stepId: string): boolean
{
	let connected = false;
	traverseGrapesProject(grapesProjectData, ({component}) =>
	{
		if(component.attributes && component.attributes[GjsAttributeNames.Action] === GjsActionTypes.PreviousStep)
		{
			connected = true;
			return false; // stops the traversal
		}
	});

	return connected;
}

interface IGrapesTraverseCallbackParams
{
	component: IGrapesJSComponent,
	projectData: IGrapesJSProjectData,
}

/**
 * Traverses a GrapesJS component data structure and calls a callback for each component using depth-first search
 * 
 * The callback can return true to stop the traversal (for example, in case we were looking for a specific component and found it)
 *
 * @export
 * @param {IGrapesJSComponent} component
 * @param {IGrapesJSProjectData} projectData
 * @param {((params: IGrapesTraverseCallbackParams) => boolean | void)} callback a function to execute on each component. if it returns true, the traversal stops
 * @return {*}  {void}
 */
export function traverseGrapesComponent(
	component: IGrapesJSComponent, 
	projectData: IGrapesJSProjectData,
	callback: (params: IGrapesTraverseCallbackParams) => boolean | void
): void
{
	const stack = [component];

	while(stack.length > 0)
	{
		const currentComponent = stack.pop();
		if(!currentComponent) continue;

		// the callback can return true to stop the traversal (for example, in case we were looking for a specific component and found it)
		if(callback({ component: currentComponent, projectData: projectData }))
		{
			return;
		}

		if(currentComponent.components)
		{
			// push in reverse to maintain the order since we pop from the end of the stack
			for(let i = currentComponent.components.length - 1; i >= 0; --i)
			{
				stack.push(currentComponent.components[i]);
			}
		}
	}
}

/**
 * Traverses a GrapesJS project data structure and calls a callback for each component using depth-first search
 *
 * @export
 * @param {IGrapesJSProjectData} projectData the project data to traverse
 * @param {(params: IGrapesTraverseCallbackParams) => boolean | void} callback if it returns true, the traversal will not continue into the component's children
 * @return {*}  {void}
 */
export function traverseGrapesProject(projectData: IGrapesJSProjectData, callback: (params: IGrapesTraverseCallbackParams) => boolean | void): void
{
	if(!projectData || !projectData.pages) return;
	for(const page of projectData.pages)
	{
		if(!page.frames) continue;
		for(const frame of page.frames)
		{
			if(!frame.component) continue;
			const frameComponent = frame.component;

			if(!frameComponent.components) continue;
			for(const component of frameComponent.components)
			{
				traverseGrapesComponent(component, projectData, callback);
			}
		}
	}
}

export function getStylesForComponent(projecData: IGrapesJSProjectData, componentId: string, cssState = CssStates.Normal): IGrapesJSStyle[]
{
	const output = Array<IGrapesJSStyle>();
	if(!projecData || !projecData.styles) return output;

	for(const style of projecData.styles)
	{
		if(style?.selectors?.includes(componentId))
		{
			if(style.state === cssState || (!style.state && cssState === CssStates.Normal))
			{
				output.push(style);
			}
		}
	}

	return output;
}

export function setGrapesProjectStylesForComponent(projectData: IGrapesJSProjectData, componentId: string, styles): void
{
	if(!projectData || !projectData.styles) return;

	let styleWasSet = false;
	for(const style of projectData.styles)
	{
		if(style?.selectors?.includes(componentId))
		{
			style.style = { ...styles };
			styleWasSet = true;
		}
	}

	if(!styleWasSet)
	{
		projectData.styles.push({
			selectors: [componentId],
			style: { ...styles },
		});
	}
}

export async function RenderGrapesToHTML(grapesProjectData: IGrapesJSProjectData, renderMode: GrapesRenderModes): Promise<string>
{
	if(!grapesProjectData) return basicHTML;
	if(!grapesRenderer) createGrapesRenderer();

	resetGrapesJSEditor(grapesRenderer);
	const data = JsonCopy(grapesProjectData);
	fixGrapesPreviewUrls(data);
	grapesRenderer.loadProjectData(data);
	const htmlWithTextVariables = processTextVariables(grapesRenderer.getHtml().replace("<body>", "").replace("</body>", ""), renderMode);
	let output = await processHTML(`<html>
		<head>
			<meta name="transparent" content="true">
   			<meta charset="utf-8">
			<style>
				${commonCSS}
				${processCSS(grapesRenderer.getCss() ?? "", renderMode)}
			</style>
		</head>
		<body>
			${htmlWithTextVariables}
		<script>
			${getDefaultJS(renderMode)}
			${generateEventList(grapesRenderer.getHtml(), renderMode)}
			${processTextVariablesFunction(htmlWithTextVariables, renderMode)}
			${grapesRenderer.getJs()}
		</script>
		</body>
	</html>`,
	renderMode);

	output = fixEscapedPatterns(output);
	return output;
}

/**
 * Replaces certain patterns of escaped characters in the htmlString with their unescaped counterparts  
 * for example, replacing `&lt;br /&gt;` with `<br />`
 *
 * @export
 * @param {string} htmlString
 * @param {string} [patterns={
 * 	"&lt;br /&gt;": "<br />",
 * }]
 * @return {*}  {string}
 */
export function fixEscapedPatterns(htmlString: string, patterns?: Record<string, string>): string
{
	const patternsToFix = {
		"&lt;": "<",
		"&gt;": ">",
		...patterns,
	};

	let output = htmlString;
	for(const [escaped, unescaped] of Object.entries(patternsToFix))
	{
		output = output.replace(new RegExp(escaped, "g"), unescaped);
	}
	return output;
}

export function processCSS(inputString: string, renderMode: GrapesRenderModes) : string
{
	let outputString = inputString;
	
	if(renderMode === GrapesRenderModes.Publish)
	{
		// only replace background image urls in publish mode
		outputString = processCSSBackgroundImage(outputString); // replace background-image properties with localized asset urls
	}
	outputString = processCSSStates(outputString); // sort :hover, :active so that :hover is always before :active
	outputString = removeCSSMediaQueries(outputString); // there should be only one media query, so we can just remove it

	return outputString;
}

export function processTextVariables(htmlString: string, renderMode: GrapesRenderModes): string
{
	if(renderMode !== GrapesRenderModes.Publish) return htmlString;

	function processNode(node: Node)
	{
		if(node.nodeValue)
		{
			const splitData = TextVariables.breakToWords(node.nodeValue);			
			for (let i = 0; i < splitData.words.length; i++) 
			{
				if (splitData.splitters[i].endsWith(TextVariables.variableMarker)) 
				{
					splitData.splitters[i] = splitData.splitters[i].replace(TextVariables.variableMarker, '');
					const varText = TextVariables.variableMarker + splitData.words[i];
					splitData.words[i] = `<span id='textvar_${textVariablesCounter}'>${varText}</span>`;
					textVariablesCounter++;

					node.nodeValue = TextVariables.buildFromWords(splitData.words, splitData.splitters);
				}
			}
		}

		if(node.hasChildNodes())
		{
			for(const child of node.childNodes)
			{
				processNode(child);
			}
		}
	}

	let textVariablesCounter = 0;
	const parsed = new DOMParser().parseFromString(htmlString, "text/html");
	processNode(parsed);

	return fixEscapedPatterns(parsed.body.innerHTML);
}

export function processTextVariablesFunction(htmlString: string, renderMode: GrapesRenderModes) : string
{
	if(renderMode !== GrapesRenderModes.Publish) return '';

	const splitData = TextVariables.breakToWords(htmlString);
	let textVariablesCounter = 0;
	const textVarMap: { [key: string]: string[] } = {};
	for (let i = 0; i < splitData.words.length; i++) 
	{
		if (splitData.splitters[i].endsWith(TextVariables.variableMarker)) 
		{
			splitData.splitters[i] = splitData.splitters[i].replace(TextVariables.variableMarker, '');
			const varText = TextVariables.variableMarker + splitData.words[i];
			if(!textVarMap[varText]) textVarMap[varText] = [];
			textVarMap[varText].push(`textvar_${textVariablesCounter}`);
			textVariablesCounter++;
		}
	}

	return `
		function setTextVariableData(variableName, data)
		{
			const textVarMap = ${JSON.stringify(textVarMap)};
			for(const [textVar, idList] of Object.entries(textVarMap))
			{
				if(textVar === variableName)
				{
					for(const id of idList)
						{
							const textVarElement = document.getElementById(id);
							if(!textVarElement) continue;
							textVarElement.innerHTML = data;
						}
				}
			}
		}

		function getTextVariablesInDocument()
		{
			return ${JSON.stringify(Object.keys(textVarMap))};
		}
	`;
}

interface IPageEvent {
	eventName: string;
	activationText: string;
}

export function generateEventList(htmlString: string, renderMode: GrapesRenderModes) : string
{
	if(renderMode !== GrapesRenderModes.Publish) return '';

	const seenEvents = new Set<string>();
	const eventList = new Array<IPageEvent>();

	const parsed = new DOMParser().parseFromString(htmlString, "text/html");
	const elementsWithActions = parsed.querySelectorAll(`[${GjsAttributeNames.Action}]`);
	for(const element of elementsWithActions)
	{
		const eventName = element.getAttribute(GjsAttributeNames.Action);

		if(!eventName) continue; // skip actions that aren't actually defined
		if(seenEvents.has(eventName)) continue; // prevent duplicate actions
		seenEvents.add(eventName);

		eventList.push({
			eventName: eventName,
			activationText: element.getAttribute(GjsAttributeNames.ActivationText) || "",
		});
	}

	return `
		function getPageEvents()
		{
			return ${JSON.stringify(eventList)};
		}
	`;
}

export async function processHTML(htmlString: string, renderMode: GrapesRenderModes) : Promise<string>
{
	const parsed = new DOMParser().parseFromString(htmlString, "text/html");
	processNewlines(parsed, renderMode);
	processHTMLFunctions(parsed, renderMode);

	if(renderMode === GrapesRenderModes.Publish)
	{
		await processHTMLImages(parsed);
	}

	return parsed.documentElement.outerHTML;
}

export function processNewlines(node, renderMode: GrapesRenderModes)
{
	if(renderMode !== GrapesRenderModes.Publish) return; // no need to do this in simulation mode

	const tagNamesWithTextInside = new Set(["p", "span", "div", "text", "b", "i"]);

	if(node.nodeType === Node.TEXT_NODE)
	{
		if(tagNamesWithTextInside.has(node.parentElement?.tagName?.toLowerCase()))
		{
			if(node.nodeValue.trim().length)
			{
				node.nodeValue = node.nodeValue.replace(/(\r\n|\n|\r)/gm, "<br />");
			}
		}
	}

	if(node.childNodes)
	{
		for(const child of node.childNodes)
		{
			processNewlines(child, renderMode);
		}
	}
}

/**
 * Removes native html event attributes such as `onpaste` or `onmousedown` from the given node
 *
 * @export
 * @param {*} node
 * @return {*} 
 */
export function stripNativeHtmlAttributes(node)
{
	if(!node?.attributes) return;

	nativeHtmlEventAttributes.forEach((attribute) => node.removeAttribute(attribute));
}

export function processHTMLFunctions(node, renderMode: GrapesRenderModes)
{
	if(node.attributes)
	{
		// native html events such as "onclick", "onpaste" etc. these were added for use with grapesjs and are not intended to be used in the final output html
		stripNativeHtmlAttributes(node);

		// apply attributes as necessary
		for(const attribute of node.attributes)
		{
			// construct proper Vuplex message invocation
			if(attribute.name === GjsAttributeNames.Action)
			{
				const functionType = attribute.value;
				switch(functionType)
				{
					case GjsActionTypes.MoveToStep:
						const stepIdAttribute = node.attributes.getNamedItem(GjsAttributeNames.ParamStepID);
						node.setAttribute("onclick", `sendMessageToUnity({ type: '${GjsActionTypes.MoveToStep}', stepId: '${stepIdAttribute?.value}' })`);
						node.removeAttribute(GjsAttributeNames.ParamStepID);
						break;
					case GjsActionTypes.NextStep:
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.NextStep}' })`);
						break;
					case GjsActionTypes.PreviousStep:
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.PreviousStep}' })`);
						break;
					case GjsActionTypes.OpenURL:
						const urlAttribute = node.attributes.getNamedItem(GjsAttributeNames.ParamURL);
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.OpenURL}', url: '${urlAttribute?.value}' })`);
						node.removeAttribute(GjsAttributeNames.ParamURL);
						break;
					case GjsActionTypes.RetryQuiz:
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.RetryQuiz}' })`);
						break;
					case GjsActionTypes.CompleteQuiz:
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.CompleteQuiz}' })`);
						break;
					case GjsActionTypes.FinishProcedure:
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.FinishProcedure}' })`);
						break;
					case GjsActionTypes.OpenAttachment:
						const assetIdAttribute = node.attributes.getNamedItem(GjsAttributeNames.AssetID);
						const attachmentAssetId = assetIdAttribute?.value;
						let attachmentAssetUrl = "";
						switch(renderMode)
						{
							case GrapesRenderModes.Preview:
								attachmentAssetUrl = AssetUtility.getRawAssetURL(attachmentAssetId ?? "");								
								break;
							case GrapesRenderModes.Publish:								
								const attachmentAssetName = AssetUtility.getAssetName(attachmentAssetId ?? "");
								const attachmentAssetExtension = Utils.getFileExtension(attachmentAssetName ?? "");
								attachmentAssetUrl = `${attachmentAssetId}.${attachmentAssetExtension}`;
								break;
							default:
								console.warn("unknown render mode", renderMode);
								break;
						}
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.OpenAttachment}', path: '${attachmentAssetUrl}' })`);
						node.removeAttribute(GjsAttributeNames.AssetID);
						break;
					case GjsActionTypes.ScenarioLink:
						const scenarioInfoAttribute = node.attributes.getNamedItem(GjsAttributeNames.ParamScenario);
						const scenarioInfo = JSON.parse(scenarioInfoAttribute?.value ?? "{}") as IScenarioPickerInfo;
						const scenarioModeAttribute = node.attributes.getNamedItem(GjsAttributeNames.ParamScenarioMode);
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ 
							type: '${GjsActionTypes.ScenarioLink}', 
							${scenarioInfo.nodeId ? 'scenarioId:' + scenarioInfo.nodeId + ',' : ""}
							scenarioName: '${scenarioInfo.label}', 
							mode: '${scenarioModeAttribute?.value}',
						})`);
						node.removeAttribute(GjsAttributeNames.ParamScenario);
						node.removeAttribute(GjsAttributeNames.ParamScenarioMode);
						break;
					case GjsActionTypes.PlayVideo:
						// show video overlay component in editor and autoplay video
						const videoAssetId = node.attributes.getNamedItem(GjsAttributeNames.AssetID)?.value;
						const videoAssetName = AssetUtility.getAssetName(videoAssetId ?? "");
						const videoAssetExtension = Utils.getFileExtension(videoAssetName ?? "");
						const videoAssetUrl = renderMode === GrapesRenderModes.Publish ? `${videoAssetId}.${videoAssetExtension}` : AssetUtility.getPreviewAssetURL(videoAssetId ?? "");
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.PlayVideo}', path: '${videoAssetUrl}', elementId: '${node.id}' })`);
						node.setAttribute(GjsAttributeNames.AssetPath, videoAssetUrl);
						node.removeAttribute(GjsAttributeNames.AssetID);
						break;
					case GjsActionTypes.PlayAudio:
						const audioAssetId = node.attributes.getNamedItem(GjsAttributeNames.AssetID)?.value;
						const audioAssetName = AssetUtility.getAssetName(audioAssetId ?? "");
						const audioAssetExtension = Utils.getFileExtension(audioAssetName ?? "");
						const audioAssetUrl = renderMode === GrapesRenderModes.Publish ? `${audioAssetId}.${audioAssetExtension}` : AssetUtility.getPreviewAssetURL(audioAssetId ?? "");
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.PlayAudio}', path: '${audioAssetUrl}', elementId: '${node.id}' })`);
						node.setAttribute(GjsAttributeNames.AssetPath, audioAssetUrl);
						node.removeAttribute(GjsAttributeNames.AssetID);
						break;
					case GjsActionTypes.EnlargeCamera:
						const cameraId: string = node.attributes.getNamedItem(GjsAttributeNames.ParamCameraID)?.value ?? "";
						switch(renderMode)
						{
							case GrapesRenderModes.Publish:
								const nodeIndex = ProjectPacker.getInstance().objectGuidToNodeIndexMap.get(cameraId) ?? -1;
								const cameraIndex = ProjectPacker.getInstance().nodeIndexToCameraIndex.get(nodeIndex);
								if(cameraIndex !== undefined)
								{
									node.setAttribute(GjsAttributeNames.ParamCameraID, cameraIndex.toString());
								}
								break;
							case GrapesRenderModes.Preview:
								node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.EnlargeCamera}', elementId: '${node.id}', cameraId: '${cameraId}' })`);
								break;
						}
						break;
					case GjsActionTypes.FullScreen:
						const imageAssetId = node.attributes.getNamedItem(GjsAttributeNames.AssetID)?.value;
						const imageAssetName = AssetUtility.getAssetName(imageAssetId ?? "");
						const imageAssetExtension = Utils.getFileExtension(imageAssetName ?? "");
						const imageAssetUrl = renderMode === GrapesRenderModes.Publish ? `${imageAssetId}.${imageAssetExtension}` : AssetUtility.getPreviewAssetURL(imageAssetId ?? "");
						node.setAttribute(GjsAttributeNames.OnClick, `sendMessageToUnity({ type: '${GjsActionTypes.FullScreen}', elementId: '${node.id}', path: '${imageAssetUrl}' })`);
						break;
					default:
						//console.warn("unknown function type: " + functionType);
						break;
				}

				node.removeAttribute(attribute.name);
			}
		}
	}

	if(node.childNodes)
	{
		for(const childNode of node.childNodes)
		{
			processHTMLFunctions(childNode, renderMode);
		}
	}
}

/**
 * Processes HTML image nodes and replaces them with localized asset urls
 *
 * @param {*} node
 */
export async function processHTMLImages(node)
{
	if(node.attributes)
	{
		// first we check for cover image id, as that means the AssetID attribute is saved for video/audio/etc
		const coverImageId = node.attributes.getNamedItem(GjsAttributeNames.CoverAssetID)?.value;
		if(coverImageId)
		{
			const coverImageExtension = await Utils.fetchAssetFileExtension(coverImageId);
			const coverImageUrl = `${coverImageId}.${coverImageExtension.toLowerCase()}`;
			node.setAttribute("style", `background-image: url(${coverImageUrl});`);
			node.removeAttribute(GjsAttributeNames.CoverAssetID);
		}
		// if there is no cover image, we check for AssetID as it's used for both IMG nodes and other nodes with background image in the CSS
		else
		{
			const imageId = node.attributes.getNamedItem(GjsAttributeNames.AssetID)?.value;
			if(imageId)
			{
				const imageExtension = await Utils.fetchAssetFileExtension(imageId);
				{
					const imageUrl = `${imageId}.${imageExtension.toLowerCase()}`;
					if(node.nodeName.toLowerCase() === "img")
					{
						node.setAttribute("src", imageUrl);
						node.setAttribute("dragable", "false");						
					}
					else
					{
						// we want to add the background-image style, not overwrite the whole style attribute, in case it was set elsewhere
						const currentStyle = node.getAttribute("style");
						node.setAttribute("style", `${currentStyle ? currentStyle + ";" : ""}background-image: url(${imageUrl});`);
					}
					node.removeAttribute(GjsAttributeNames.AssetID);
				}
			}
		}
	}

	// process child nodes recursively
	if(node.childNodes)
	{
		for(const chileNode of node.childNodes)
		{
			await processHTMLImages(chileNode);
		}
	}
}

/**
 * Processes CSS background-image properties and replaces them with localized asset urls
 *
 * @param {string} cssString
 * @returns
 */
export function processCSSBackgroundImage(cssString: string): string
{
	let outputString = cssString;
	let index = 0;
	while(index < outputString.length)
	{
		index = outputString.indexOf("url(", index);
		if(index === -1) break;

		const urlStartIndex = index + 4; // 4 is the length of "url("
		const urlEndIndex = outputString.indexOf(")", urlStartIndex);
		assert(urlEndIndex !== -1, "Malformed CSS string - url is not closed");
		if(urlEndIndex === -1) break; // assert above should throw but we need to break for mocked tests or we get an infinite loop

		const url = outputString.substring(urlStartIndex, urlEndIndex);
		const urlParams = Object.fromEntries(new URLSearchParams(url));
		const assetId = urlParams["asset"];
		if(assetId)
		{
			const assetName = AssetUtility.getAssetName(assetId);
			const assetExtension = Utils.getFileExtension(assetName ?? "");
			const assetUrl = `${assetId}.${assetExtension}`;
			outputString = outputString.replace(url, assetUrl);
		}

		index = outputString.indexOf(")", urlStartIndex); // we modified the string, so we need to search from the closing parenthesis now
	}

	return outputString;
}

/**
 * Sorts :hover, :active so that :hover is always before :active
 *
 * @param {string} cssString
 * @returns
 */
export function processCSSStates(cssString: string): string
{
	let outputString = cssString;

	// we have a media query that all component-specific rules should be living under
	// what we do, then, is find the inner string of the media query, and sort the rules inside it
	// the way to sort the rules, then, is to split the rules by the closing } 
	// then we need to find the selector itself, aka the string before the opening {
	// then we need to find the state, aka the string after the first : and before the opening bracket - not sure how classes are laid out yet
	// we figure out what state it is - hover or active - and we add the rules to the appropriate array
	// then we reconstruct the string by adding the rules back in the correct order, and re-assembling the before-media, media-inner, and after-media strings
	// we now have a sorted string, and we can return it

	// extract media query string
	let beforeMediaString = "";
	let mediaInnerString = "";
	let afterMediaString = "";
	const mediaQueryStartIndex = outputString.indexOf("@media");
	if(mediaQueryStartIndex === -1) 
	{
		mediaInnerString = outputString; // no media query, operate on the whole string
	}
	else
	{
		const mediaOpenBraceIndex = outputString.indexOf("{", mediaQueryStartIndex);
		const mediaQueryEndIndex = outputString.indexOf("}}", mediaOpenBraceIndex);
		assert(mediaQueryEndIndex !== -1, "Malformed CSS string - media query is not closed");

		beforeMediaString = outputString.substring(0, mediaOpenBraceIndex + 1); // +1 to include the opening {
		mediaInnerString = outputString.substring(mediaOpenBraceIndex + 1, mediaQueryEndIndex + 1); // +1 to include the closing }
		afterMediaString = outputString.substring(mediaQueryEndIndex + 1); // +1 to include the closing }
	}

	// split the rules by the closing }
	const rules = mediaInnerString.split("}");
		
	// sort the rules by state
	const outRules = new Array<string>();
	const hoverRules = new Array<string>();
	const activeRules = new Array<string>();
	for(const rule of rules)
	{
		if(rule.trim().length === 0) continue; // skip empty rules (usually the last one)

		// find the selector
		const selectorEndIndex = rule.indexOf("{");
		const selector = rule.substring(0, selectorEndIndex);

		// find the state
		const stateStartIndex = selector.indexOf(":");		
		if(stateStartIndex === -1) // no state, add to outRules
		{
			outRules.push(rule + "}");
		}
		else
		{
			const state = selector.substring(stateStartIndex, selectorEndIndex).trim();
			switch(state)
			{
				case ":hover":
					hoverRules.push(rule + "}");
					break;
				case ":active":
					activeRules.push(rule + "}");
					break;
				default: // unknown state, add to outRules
					//console.warn("unknown state", state);
					outRules.push(rule + "}");
					break;
			}
		}
	}

	// add the rules back in the correct order
	mediaInnerString = outRules.join(' ');
	if(hoverRules.length > 0)
	{
		mediaInnerString += hoverRules.join(' ');
	}
	if(activeRules.length > 0)
	{
		mediaInnerString += activeRules.join(' ');
	}

	// reassemble the string
	outputString = beforeMediaString + mediaInnerString + afterMediaString;	
	return outputString;
}

export function removeCSSMediaQueries(cssString: string): string
{
	/* 
		courtesy of chatGPT:
		@media[^{]+\{ - This captures the media query declaration up to its opening {.

		(([^{]+\{[^}]+\})+?) - captures:

		[^{]+ - Anything not an opening {.
		\{[^}]+\} - An entire block {...}.
		The surrounding (...)+? ensures we capture as many of these {...} blocks as exist in a non-greedy way, ensuring we capture the rules inside the media query without consuming the media query's closing brace.
		} - This captures the closing brace of the media query.
	*/
	return cssString.replace(/@media[^{]+\{(([^{]+\{[^}]+\})+?)\}/g, '$1');
}

/**
 * Deletes components from the grapes editor
 *
 * @export
 * @param {Component[]} components
 * @param {Editor} editor
 */
export function deleteGrapesComponents(components: Component[], editor: Editor, grapesProjectId?: string, raiseAction = true)
{
	grapesProjectId = grapesProjectId ?? store.getState().visageModule.editorContext.projectId;
	if(!components.length || !editor) return;

	if(!editor.getSelectedAll().length)
	{
		editor.select(components);
	}

	editor.runCommand('core:component-delete');

	if(raiseAction)
	{
		new SetGrapesProjectAction({
			grapesProjectId: grapesProjectId,
			newData: JsonCopy(editor.getProjectData()) as IGrapesJSProjectData,
			actionName: `Deleted ${components.length} UI Components`,
		});
	}

	// remove selection
	store.dispatch(StoreActions.visage.setSelectedComponentIds([]));
}

/**
 * Duplicates components in the grapes editor, placing the newly created components under the same parents as the original components
 *
 * @export
 * @param {Component[]} components
 * @param {Editor} editor
 * @param {string} grapesProjectId
 * @param {boolean} [raiseAction=true]
 */
export function duplicateGrapesComponents(components: Component[], editor: Editor, grapesProjectId?: string, raiseAction = true)
{
	if(!components?.length) return;

	const newComponentIds = new Array<string>();
	components.forEach(component =>
	{
		const newComponent: Component = component.clone();
		component.parent()!.append(newComponent);
		newComponentIds.push(newComponent.getId());
	});

	if(newComponentIds)
	{
		if(raiseAction)
		{
			new SetGrapesProjectAction({
				grapesProjectId: grapesProjectId,
				newData: JsonCopy(editor.getProjectData()) as IGrapesJSProjectData,
				actionName: `Duplicated ${newComponentIds.length} UI Components`,
			});
			
			// select the new components
			store.dispatch(StoreActions.visage.setSelectedComponentIds(newComponentIds));
		}
	}
}

/**
 * Returns the value of a CSS property for a component, taking into account classes and states
 *
 * @export
 * @param {string} propertyName
 * @param {Editor} editor
 * @param {Component} component
 * @param {string} [state=""] the state of the component, e.g. :hover or :active. defaults to empty string for "normal" state
 * @returns {string}
 */
export function getGrapesCssPropertyValue(propertyName: string, editor: Editor, component: Component, state: string): string
{
	if(!propertyName || !editor || !component) return "";

	const property = component.getStyle()[propertyName];
	if(property)
	{
		return property.toString();
	}
	else
	{
		const componentSelector = component.getSelectorsString();
		if(!componentSelector) return "";

		const componentRules = editor.CssComposer.getRules(componentSelector);
		if(componentRules.length)
		{
			const rule = componentRules.find(rule => (rule.config.state ?? "") === state);
			if(rule?.config?.style)
			{
				return rule.config.style[propertyName] ?? "";
			}
		}
	}

	return "";
}

/**
 * Sets the value of a CSS property for a component, taking into account classes and states
 *
 * @export
 * @param {string} propertyName
 * @param {Component[]} components
 * @param {string} value
 */
export function setGrapesCssPropertyValue(propertyName: string, value: string, components: Component[])
{
	components.forEach(component => 
	{
		if(!value)
		{
			component.removeStyle(propertyName);
		}
		else
		{
			component.setStyle({ ...component.getStyle(), [propertyName]: value });
		}
	});
}

/**
 *
 *
 * @export
 * @param {GjsAttributeNames} attributeName
 * @param {*} value
 * @param {Component[]} components
 */
export function setGrapesElementAttribute(attributeName: GjsAttributeNames, value: any, components: Component[])
{
	components.forEach(component => 
	{
		if(!value)
		{
			component.removeAttributes([attributeName]);
		}
		else
		{
			component.setAttributes({ ...component.getAttributes(), [attributeName]: value });
		}
	});
}

/**
 *
 *
 * @export
 * @param {GjsAttributeNames} attributeName
 * @param {Component} component
 * @return {*} 
 */
export function getGrapesElementAttributeValue(attributeName: GjsAttributeNames, component: Component)
{
	return component.getAttributes()[attributeName] ?? "";
}

/**
 * Whether or not we should render the CSS property section for all of the given components
 * the whitelist if checked first: if it is present, we skip rendering if the component type is not in the whitelist
 * the blacklist is checked second: if it is present, we skip rendering if the component type is in the blacklist
 * if the optionalProperty is present, we check if any of the associated properties are present in the component's style
 * if valueWhitelist is present, we check that the property value of each component is in the whitelist
 * only if all conditions are satisfied should we render the css property section
 * 
 * @export
 * @param {{ query: ICssPropertyRenderQuery, components?: IGrapesJSComponent[], grapesProject?: IGrapesJSProjectData, cssState?: CssStates }} params
 * @return {*}  {boolean}
 */
export function shouldRenderCssProperty(params: { 
	query: Partial<ICssPropertyRenderQuery>, 
	components?: IGrapesJSComponent[], 
	grapesProject?: IGrapesJSProjectData, 
	cssState?: CssStates
}): boolean
{
	params.components = params.components ?? store.getState().visageModule.editorContext.components;
	params.grapesProject = params.grapesProject ?? store.getState().visageModule.editorContext.projectData;
	params.cssState = params.cssState ?? store.getState().visageModule.elementState;

	for(const component of params.components)
	{
		// eheck type - whitelist takes precedence over blacklist
		if(params.query.typeWhitelist && !params.query.typeWhitelist.includes(component.type as GjsComponentTypes)) return false;
		if(params.query.typeBlacklist && params.query.typeBlacklist.includes(component.type as GjsComponentTypes)) return false;

		// if "checkTemporary" is true, we check the temporary component styles in the editor context ("permanent" styles are in the main redux storage)
		const componentStyle = params.query.checkTemporary ?
			store.getState().visageModule.editorContext.componentStyles[component.attributes.id]?.[params.cssState]
			:
			getGrapesComponentStyleFromRedux(component.attributes.id, params.grapesProject, params.cssState);
		
		// do not render if the component does not have a defined style
		if(!componentStyle) return false;

		// check added property - if even one associated property is present, we check the value whitelist and substring. if all is well we render
		if(params.query.associatedCssProperties)
		{
			for(const associatedProperty of params.query.associatedCssProperties)
			{
				const propertyValue = componentStyle[associatedProperty.name];
				if(propertyValue)
				{
					// do not render if the property value does not exist in the allowed values
					if(associatedProperty.valueWhitelist && !associatedProperty.valueWhitelist.includes(propertyValue))
					{
						return false;
					}

					if(associatedProperty.mustIncludeSubstring)
					{
						// do not render if the property value does not include the required substring
						if(!propertyValue.includes(associatedProperty.mustIncludeSubstring))
						{
							return false;
						}
					}

					// render if the property value is both allowed and has the correct substring (if any)
					return true;
				}
			}

			// do not render if the query defines associated properties but none of them are present in the component
			return false;
		}
	}

	// if we reach this point, all conditions are satisfied
	return true;
}

/**
 * Whether or not we should render the attribute property section for all of the given components
 * the whitelist if checked first: if it is present, we skip rendering if the component type is not in the whitelist
 * the blacklist is checked second: if it is present, we skip rendering if the component type is in the blacklist
 * if the associatedAttributes is present, we check if all of the associated attributes are present in the component's attributes
 * only if all conditions are satisfied should we render the attribute property section
 *
 * @export
 * @param {{
 * 	query: Partial<IAttributePropertyRenderQuery>,
 * 	components?: IGrapesJSComponent[],
 * 	grapesProject?: IGrapesJSProjectData,
 * }} params
 * @return {*}  {boolean}
 */
export function shouldRenderAttributeProperty(params: {
	query: Partial<IAttributePropertyRenderQuery>,
	components?: IGrapesJSComponent[],
	grapesProject?: IGrapesJSProjectData,
}): boolean
{
	params.components = params.components ?? store.getState().visageModule.editorContext.components;
	params.grapesProject = params.grapesProject ?? store.getState().visageModule.editorContext.projectData;

	for(const component of params.components)
	{
		// check type - whitelist takes precedence over blacklist
		if(params.query.typeWhitelist && !params.query.typeWhitelist.includes(component.type as GjsComponentTypes)) return false;
		if(params.query.typeBlacklist && params.query.typeBlacklist.includes(component.type as GjsComponentTypes)) return false;

		// check attributes are present - if even one is missing, we don't render
		if(params.query.associatedAttributes)
		{
			for(const associatedAttribute of params.query.associatedAttributes)
			{
				if(!isTangible(component.attributes[associatedAttribute.name]))
				{
					return false;
				}
			}
		}
	}

	return true;
}

/**
 * 
 *
 * @export
 * @param {boolean} includeNone whether to include a "none" option in the dropdown
 * @returns an array of `IDropdownOption` for each available visage template as defined in redux
 */
export function getVisageTemplateDropdownOptions(includeNone = false)
{
	const output = Array<IDropdownOption>();
	if(includeNone) output.push({ value: "", display: "- None -" });

	for(const [key, value] of Object.entries<IVisageTemplate>(store.getState().visageModule.uiTemplateData))
	{
		output.push({ value: key, display: value.name });
	}

	return output;
}

/**
 * searches the grapes editor for components with the given ids
 *
 * @param {Editor} editor
 * @param {string[]} ids
 * @return {*}  {Component[]}
 */
export function getComponentsFromIds(editor: Editor, ids: string[])
{
	const components = new Array<Component>();
	ids.filter(id => isTangible(id) && id !== "").forEach(id =>
	{
		const component = editor.getWrapper()?.find(`#${id}`)[0];
		if(component) components.push(component);
	});
	
	return components;
}

/**
 * returns a copy of the grapes project from redux
 *
 * @export
 * @param {string} projectId
 * @param {VisageEditorWindowModes} editorMode
 * @return {*}  {IGrapesJSProjectData} 
 */
export function getGrapesProject(projectId: string, editorMode: VisageEditorWindowModes): IGrapesJSProjectData
{
	switch(editorMode)
	{
		case VisageEditorWindowModes.Step:
			return JsonCopy(store.getState().visageModule.stepUIData[projectId]);
		case VisageEditorWindowModes.Template:
			return JsonCopy(store.getState().visageModule.uiTemplateData[projectId]?.content);
		default:
			throw new Error("Unsupported editor mode: " + editorMode);
	}
}

/**
 * updates the grapes project in redux
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 * @param {string} projectId
 * @param {VisageEditorWindowModes} editorMode
 */
export function setGrapesProject(grapesProject: IGrapesJSProjectData, projectId: string, editorMode: VisageEditorWindowModes): void
{
	switch(editorMode)
	{
		case VisageEditorWindowModes.Step:
			store.dispatch(StoreActions.visage.setStepUIData({ stepId: projectId, data: JsonCopy(grapesProject) }));
			break;
		case VisageEditorWindowModes.Template:
			const newTemplateData = JsonCopy(store.getState().visageModule.uiTemplateData[projectId]);
			newTemplateData.content = JsonCopy(grapesProject);
			store.dispatch(StoreActions.visage.setUITemplateData({ templateId: projectId, data: newTemplateData }));
			break;
	}
}

/**
 * searches the redux project for a component with the given id and returns it
 *
 * @export
 * @param {string} componentId
 * @param {string} grapesProjectId
 * @param {VisageEditorWindowModes} editorMode
 * @return {*}  {(IGrapesJSComponent | undefined)} a copy of the component object
 */
export function getGrapesComponentFromRedux(componentId: string, grapesProject: IGrapesJSProjectData): IGrapesJSComponent | undefined
{
	if(!componentId || !grapesProject) return;

	const query = `$..components[?(@.attributes?.id === "${componentId}")]`;
	const component = JSONPath({ path: query, json: grapesProject })?.[0];
	if(!component) return;

	return JsonCopy(component);
}

/**
 * gets the value of the given attribute from the component in the redux project
 *
 * @export
 * @param {string} componentId
 * @param {string} attributeName
 * @param {IGrapesJSProjectData} [grapesProject]
 * @return {*}  {string}
 */
export function getGrapesComponentAttributeValueFromRedux(componentId: string, attributeName: string, grapesProject?: IGrapesJSProjectData): string
{
	grapesProject = grapesProject ?? store.getState().visageModule.editorContext.projectData;
	const component = getGrapesComponentFromRedux(componentId, grapesProject);
	return component?.attributes[attributeName] ?? "";
}

/**
 * searches the redux project for a style object for the given component, taking into account the component's state (hover, active)
 *
 * @export
 * @param {string} componentId
 * @param {IGrapesJSProjectData} grapesProject
 * @param {CssStates} cssState
 * @return {*}  {(IGrapesJSStyle | undefined)} a copy of the style object
 */
export function getGrapesComponentStyleFromRedux(componentId: string, grapesProject?: IGrapesJSProjectData, cssState?: CssStates): Record<string, string>
{
	grapesProject = grapesProject ?? store.getState().visageModule.editorContext.projectData;
	cssState = cssState ?? store.getState().visageModule.elementState;

	const output = {};
	if(!componentId || !grapesProject) return output;

	const component = getGrapesComponentFromRedux(componentId, grapesProject);
	if(!component) return output;

	const componentClassesString = component.classes?.map(c => "." + c)?.join(" ") || "";

	// get the component styles by class
	for(const style of grapesProject.styles)
	{
		for(const selector of style.selectors)
		{
			if(typeof selector === "string")
			{
				if(componentClassesString.includes(selector) && (!style.state || style.state === cssState))
				{
					Object.assign(output, style.style);
				}
			}
		}
	}

	// get the component specific style by id
	for(const style of grapesProject.styles)
	{
		// component-specific style overrides the class style
		if(style.selectors.join('').trim() === `#${componentId}`)
		{
			if(cssState === CssStates.Normal || style.state === cssState)
			{
				Object.assign(output, style.style);
				break;
			}
		}
	}

	return output;
}

/**
 * Returns the value of the given CSS property from the object's redux style object
 * Fallback to the "normal" CSS state, or return an empty string if the property is not found
 *
 * @export
 * @param {string} componentId
 * @param {CssProperties} propName
 * @param {CssStates} [cssState]
 * @return {*}  {string}
 */
export function getGrapesCssPropValue(componentId: string, propName: CssProperties, cssState?: CssStates, grapesProject?: IGrapesJSProjectData): string
{
	cssState = cssState ?? store.getState().visageModule.elementState;
	let style = getGrapesComponentStyleFromRedux(componentId, grapesProject, cssState);
	if(style[propName])
	{
		return style[propName];
	}
	else
	{
		// in case the property is not found in the component's style, we fallback to the 'normal' state
		// if it's still not there, we return an empty string
		style = getGrapesComponentStyleFromRedux(componentId, grapesProject, CssStates.Normal);
		return style[propName] || "";
	}
}

/**
 * A small utility to generate an array of `IActionValuesChange` objects for a given css property and value
 * Used for undo/redo actions
 *
 * @export
 * @param {CssProperties} propName
 * @param {string} newValueFill
 * @param {IGrapesJSComponent[]} [components]
 * @return {*}  {IActionValuesChange}
 */
export function getGrapesCssPropChanges(propName: CssProperties, newValueFill: string, components?: IGrapesJSComponent[], cssState = CssStates.Normal, grapesProject?: IGrapesJSProjectData): IActionValuesChange
{
	components = components ?? store.getState().visageModule.editorContext.components;

	return {
		name: propName,
		oldValues: components.map(c => getGrapesCssPropValue(c.attributes.id, propName, cssState, grapesProject)),
		newValues: new Array(components.length).fill(newValueFill),
	}
}

/**
 * Returns the value of the given CSS property from the editor context in redux
 * The context is the temporary state used mostly by the visage inspector and will not immediately affect the actual project data
 * Includes a fallback to the "normal" CSS state in case the prop does not exist on the given state
 * Returns an empty string if the property is not found at all
 *
 * @export
 * @param {string} componentId
 * @param {CssProperties} propName
 * @param {CssStates} [cssState]
 * @return {*} 
 */
export function getGrapesContextCssProp(componentId: string, propName: CssProperties, cssState?: CssStates)
{
	cssState = cssState ?? store.getState().visageModule.elementState;
	const componentStyles = store.getState().visageModule.editorContext.componentStyles;

	const style = componentStyles[componentId];
	return style?.[cssState]?.[propName] || style?.[CssStates.Normal]?.[propName] || "";
}

/**
 * Goes through all components in the project and replaces grapesjs `text` components with our implementation
 * 
 * **Important:** Modifies the given project data in-place, so make sure to pass a writable object!
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 * @return {*} 
 */
export function replaceGrapesTextComponents(grapesProject: IGrapesJSProjectData): void
{
	function getContentRecursive(component: IGrapesJSComponent): string
	{
		const includeTag = ["b", "i"].includes(component.tagName?.toLowerCase() ?? "");

		let output = includeTag ? `<${component.tagName}>` : "";
		if(component.content)
		{
			output += component.content;
		}

		if(component.components)
		{
			for(const child of component.components)
			{
				output += getContentRecursive(child);
			}
		}

		if(includeTag)
		{
			output += `</${component.tagName}>`;
		}

		return output;
	}
	
	const textComponents = JSONPath<IGrapesJSComponent[]>({ path: "$..components[?(@.type === 'text')]", json: grapesProject });
	if(textComponents?.length)
	{
		for(const textComponent of textComponents)
		{
			// remove the tag, we don't need it
			delete textComponent.tagName;

			// change the type to new text type
			textComponent.type = GjsComponentTypes.Text;			

			// now we need to convert all child text-nodes into this new text component's "content" property
			textComponent.content = getContentRecursive(textComponent);

			// remove all child components
			delete textComponent.components;
		}
	}
}

/**
 * Searches the given project structure for a component that contains the given component id in its children array
 * 
 * **Important:** Returns a reference to the original object, not a copy, so make sure to pass a writable project object if you intend to modify it!
 *
 * @export
 * @param {string} componentId
 * @param {IGrapesJSProjectData} grapesProject
 * @return {*}  {(IGrapesJSComponent | undefined)}
 */
export function getGrapesComponentParentFromRedux(componentId: string, grapesProject: IGrapesJSProjectData): IGrapesJSComponent | undefined
{
	let parent;

	traverseGrapesProject(grapesProject, ({component}) =>
	{
		if(!parent && component.components?.some(c => c.attributes.id === componentId))
		{
			parent = component;
		}
		
		return parent !== undefined; // cut the traversal short if we found the parent
	});

	return parent;
}

/**
 * Given a component's style object, determines the anchor point based on the position and transform properties
 *
 * @export
 * @param {Record<string, string>} componentStyle
 * @return {*}  {ContentAnchors}
 */
export function getGrapesComponentPositionAnchor(componentStyle: Record<string, string>): ContentAnchors
{
	const left = componentStyle[CssProperties.Left];
	const right = componentStyle[CssProperties.Right];
	const top = componentStyle[CssProperties.Top];
	const bottom = componentStyle[CssProperties.Bottom];
	const transform = componentStyle[CssProperties.Transform];
	const translateX = transform?.match(/translateX\((.*?)\)/);
	const translateY = transform?.match(/translateY\((.*?)\)/);

	if(translateX)
	{
		if(translateY)
			return ContentAnchors.MiddleCenter;
		else if(top)
			return ContentAnchors.UpperCenter;
		else if(bottom)
			return ContentAnchors.LowerCenter;
	}
	else if (translateY)
	{
		if(left)
			return ContentAnchors.MiddleLeft;
		else if(right)
			return ContentAnchors.MiddleRight;
	}
	else if (left)
	{
		if(top)
			return ContentAnchors.UpperLeft;
		else if(bottom)
			return ContentAnchors.LowerLeft;
	}
	else if (right)
	{
		if(top)
			return ContentAnchors.UpperRight;
		else if(bottom)
			return ContentAnchors.LowerRight;
	}

	return ContentAnchors.None;
}

/**
 * Designed for use with the new position format where it's possible to have a compound value like calc(50% + 0vw)
 *
 * @export
 * @param {string} value
 * @return {*}  {ICssNumericValue}
 */
export function extractPositionValue(value: string): ICssNumericValue
{
	if(!value.includes("calc"))
	{
		const parsedValue = TextUtils.parseCssUnitString(value);
		return { value: parsedValue.value, unit: enumFromString(CssUnits, parsedValue.unit) ?? CssUnits.vw };
	}

	// calc must have a space before the value, e.g. "calc(100% + -10vw)"
	// calc must have a closing parenthesis, and we should always follow the format "calc(N% + Mvw)"
	const extractedValue = value.substring(value.lastIndexOf(" ") + 1, value.lastIndexOf(")"));
	const parsedValue = TextUtils.parseCssUnitString(extractedValue);
	//console.log(extractedValue, parsedValue);
	return { value: parsedValue.value, unit: enumFromString(CssUnits, parsedValue.unit) ?? CssUnits.vw };
}

/**
 * Iterates on the given project's styles and updates the position and transform values to match the desired format
 * 
 * **Important:** Modifies the given project data in-place, so make sure to pass a writable object!
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 * @return {*} 
 */
export function upgradeGrapesPositionFormat(grapesProject: IGrapesJSProjectData)
{
	if(!grapesProject?.styles?.length) return;

	for(const grapesStyle of grapesProject.styles)
	{
		// we need to retain the scale value if it exists
		const scale = grapesStyle.style[CssProperties.Transform]?.match(/scale\((.*?)\)/)?.[1];

		// non-absolute positions shouldn't define offsets
		if(grapesStyle.style.position !== CssPositionTypes.Absolute)
		{
			delete grapesStyle.style[CssProperties.Left];
			delete grapesStyle.style[CssProperties.Top];
			delete grapesStyle.style[CssProperties.Right];
			delete grapesStyle.style[CssProperties.Bottom];
			
			if(!scale) delete grapesStyle.style[CssProperties.Transform];
		}
		else
		{
			//extract transform values
			let transform = grapesStyle.style[CssProperties.Transform];
			const translateX = transform?.match(/translateX\((.*?)\)/);
			const translateY = transform?.match(/translateY\((.*?)\)/);
			const parsedOffsetX = TextUtils.parseCssUnitString(translateX?.[1]);
			const parsedOffsetY = TextUtils.parseCssUnitString(translateY?.[1]);

			// upper-x
			if(grapesStyle.style[CssProperties.Top])
			{
				// apply vertical offset
				const parsedTop = extractPositionValue(grapesStyle.style[CssProperties.Top]);
				if(parsedOffsetY.value && parsedOffsetY.unit !== CssUnits.percent)
				{
					grapesStyle.style[CssProperties.Top] = `${Number(parsedTop.value) + Number(parsedOffsetY.value)}${parsedTop.unit || parsedOffsetY.unit}`;
					transform = transform.replace(translateY![0], "").trim();
				}
			}
			// lower-x
			else if (grapesStyle.style[CssProperties.Bottom])
			{				
				// apply vertical offset
				const parsedTop = extractPositionValue(grapesStyle.style[CssProperties.Bottom]);
				if(parsedOffsetY.value && parsedOffsetY.unit !== CssUnits.percent)
				{
					grapesStyle.style[CssProperties.Bottom] = `${Number(parsedTop.value) + Number(parsedOffsetY.value)}${parsedTop.unit || parsedOffsetY.unit}`;
					transform = transform.replace(translateY![0], "").trim();
				}
			}
			// middle-x
			else
			{
				// set as middle depending on offsetY
				if(parsedOffsetY.value && parsedOffsetY.unit !== CssUnits.percent)
				{
					grapesStyle.style[CssProperties.Top] = `calc(50% + ${Number(parsedOffsetY.value)}${parsedOffsetY.unit})`;
					transform = transform.replace(translateY![0], "translateY(-50%)").trim();
				}
				else
				{
					grapesStyle.style[CssProperties.Top] = "calc(50% + 0vw)";
					transform = (transform || "") + " translateY(-50%)";
				}
			}

			// y-Right
			if(grapesStyle.style[CssProperties.Right])
			{
				const parsedRight = extractPositionValue(grapesStyle.style[CssProperties.Right]);

				// we should not have a transform value for corner values, so combine and remove
				if(parsedOffsetX.value && parsedOffsetX.unit !== CssUnits.percent)
				{
					grapesStyle.style[CssProperties.Right] = `${Number(parsedRight.value || "0") + Number(parsedOffsetX.value)}${parsedRight.unit || parsedOffsetX.unit}`;
					transform = transform.replace(translateX![0], "").trim();
				}
			}
			// y-Left
			else if (grapesStyle.style[CssProperties.Left])
			{
				const parsedLeft = extractPositionValue(grapesStyle.style[CssProperties.Left]);

				// should combine and remove transform on corner value
				if(parsedOffsetX.value && parsedOffsetX.unit !== CssUnits.percent)
				{
					grapesStyle.style[CssProperties.Left] = `${Number(parsedLeft.value || "0") + Number(parsedOffsetX.value)}${parsedLeft.unit || parsedOffsetX.unit}`;
					transform = transform.replace(translateX![0], "").trim();
				}
			}
			// y-Center
			else
			{
				// set as center depending on offsetX
				if(parsedOffsetX.value && parsedOffsetX.unit !== CssUnits.percent)
				{
					grapesStyle.style[CssProperties.Left] = `calc(50% + ${Number(parsedOffsetX.value)}${parsedOffsetX.unit})`;
					transform = transform.replace(translateX![0], "translateX(-50%)").trim();
				}
				else
				{
					grapesStyle.style[CssProperties.Left] = "calc(50% + 0vw)";
					transform = (transform || "") + " translateX(-50%)";
				}
			}

			transform = scale ? `scale(${scale}) ${transform}`.trim() : transform?.trim();

			// remove the transform if it's empty by now
			if(!transform)
			{
				delete grapesStyle.style[CssProperties.Transform];
			}
			else
			{
				grapesStyle.style[CssProperties.Transform] = transform.trim();
			}
		}
	}
}

const visageTypeToGjsTypeMap = {
	[VisageComponentTypes.Container]: GjsComponentTypes.Div,
	[VisageComponentTypes.Text]: GjsComponentTypes.Text,
	[VisageComponentTypes.Image]: GjsComponentTypes.Image,
	[VisageComponentTypes.Audio]: GjsComponentTypes.Audio,
	[VisageComponentTypes.Video]: GjsComponentTypes.Video,
	[VisageComponentTypes.Camera]: GjsComponentTypes.Camera,
};

/**
 * Converts a visage component definition to a grapesjs component definition
 * 
 * Styles are extracted from the component's hierarchy and are populated in the `outStyles` array, 
 * which can then be merged with a grapes project's styles array
 *
 * @export
 * @param {IVisageComponentDefinition} componentDefinition
 * @param {Array<IGrapesJSStyle>} outStyles
 * @return {*}  {(IGrapesJSComponent | undefined)}
 */
export function convertVisageComponentToGrapes(componentDefinition: IVisageComponentDefinition, outStyles: Array<IGrapesJSStyle>): IGrapesJSComponent | undefined
{
	if(!componentDefinition) return;

	// generate a unique id for the component - although we setup grapes to generate these automatically, we need this specific id for style generation
	const componentId = "C" + getUuidv4();

	// add component styles to the editor
	if(componentDefinition.style || componentDefinition.styleClassList)
	{
		// ensure the component style object structure is correct
		componentDefinition.style = { ...{ normal: {}, hover: {}, active: {} }, ...componentDefinition.style };

		// apply style classes
		if(componentDefinition.styleClassList)
		{
			const classObjects = store.getState().visageModule.blockDefinitions.styleClasses || {};
			
			for(const styleClass of componentDefinition.styleClassList)
			{
				const styleClassObject = classObjects[styleClass];
				if(!styleClassObject) continue;

				// special case for the transform property since it's used for both scale and position
				mergeCssTransformProperty(componentDefinition.style, styleClassObject);

				// apply class style; component overwrites the class
				if(styleClassObject[VisageStyleStates.Normal]) componentDefinition.style.normal = { ...styleClassObject[VisageStyleStates.Normal], ...componentDefinition.style.normal };
				if(styleClassObject[VisageStyleStates.Hover]) componentDefinition.style.hover = { ...styleClassObject[VisageStyleStates.Hover], ...componentDefinition.style.hover };
				if(styleClassObject[VisageStyleStates.Active]) componentDefinition.style.active = { ...styleClassObject[VisageStyleStates.Active], ...componentDefinition.style.active };
			}
		}

		// apply the component styles to the editor; we can ! here because we already set the default values above
		if(Object.keys(componentDefinition.style.normal!).length)
		{
			outStyles.push({ selectors: [`#${componentId}`], style: { ...componentDefinition.style.normal }, state: CssStates.Normal });
		}
		if(Object.keys(componentDefinition.style.hover!).length)
		{
			outStyles.push({ selectors: [`#${componentId}`], style: { ...componentDefinition.style.hover }, state: CssStates.Hover });
		}
		if(Object.keys(componentDefinition.style.active!).length)
		{
			outStyles.push({ selectors: [`#${componentId}`], style: { ...componentDefinition.style.active }, state: CssStates.Active });
		}
	}

	// returns a grapes component definition that includes the component and all its children (recursively)
	const childComponents = new Array<IGrapesJSComponent>();
	if(componentDefinition.children)
	{
		for(const childDefinition of componentDefinition.children)
		{
			const convertedComponent = convertVisageComponentToGrapes(childDefinition, outStyles);
			if(convertedComponent)
			{
				childComponents.push(convertedComponent);
			}
		}
	}

	return {
		name: componentDefinition.displayName,
		type: visageTypeToGjsTypeMap[componentDefinition.type],
		attributes: {
			id: componentId,
			[GjsAttributeNames.Action]: componentDefinition.actions?.at(0),
			...componentDefinition.htmlAttributes,
		},
		...(componentDefinition.type === VisageComponentTypes.Text && { content: componentDefinition.textContent }),
		components: [...childComponents],
	};
}

/**
 * **WARNING!! this function modifies the project data istself and is only intended for use here!! extracted only for unit testing and readability!! WARNING!!**
 *
 * @param {IGrapesJSProjectData} grapesProject
 * @return {*} 
 */
function extractProjectStylesIntoMap(grapesProject: IGrapesJSProjectData)
{
	const output = new Map<string, IVisageComponentStyle>();
	for(let i = grapesProject.styles.length - 1; i >= 0 ; i--)
	{
		// remove empty entries
		const style = grapesProject.styles[i];
		if(!style) 
		{
			grapesProject.styles.splice(i, 1);
			continue;
		}

		// remove entries without any selectors or selectorsAdd
		if(!style.selectors.length && !style.selectorsAdd?.length)
		{
			grapesProject.styles.splice(i, 1);
			continue;
		}

		// remove entries where the selectors are objects. these were generated by the grapesjs original editor and are WAY obsolete
		if(style.selectors.some(s => typeof s === "object"))
		{
			grapesProject.styles.splice(i, 1);
			continue;
		}

		// skip entries without selectors (they may still have selectorsAdd such as body, *, etc.)
		const styleKey = style.selectors.join(" ");
		if(!styleKey) continue;

		// create map enrty if necessary
		if(!output.has(styleKey))
		{
			output.set(styleKey, { normal: {}, hover: {}, active: {} });
		}

		// the style map should have the object set up by now
		const classStyle = output.get(styleKey)!;

		// set the styles for the map. no/empty state means normal state
		if(!style.state)
		{
			classStyle.normal = style.style;
		}
		else
		{
			switch(style.state)
			{
				case CssStates.Hover:
					classStyle.hover = style.style;
					break;
				case CssStates.Active:
					classStyle.active = style.style;
					break;
			}
		}

		// remove the style from the project now that we have it in the map (we'll be adding it back later)
		grapesProject.styles.splice(i, 1);
	}

	return output;
}

/**
 * **WARNING!! this function modifies the project data istself and is only intended for use here!! extracted only for unit testing and readability!! WARNING!!**  
 * specialized function for merging the transform property of a component with the one from the style  
 * modifies the componentSyle directly!  
 * intended for use as part of other functions only!
 *
 * @param {IVisageComponentStyle} componentStyle
 * @param {IVisageComponentStyle} classStyle
 */
function mergeCssTransformProperty(componentStyle: IVisageComponentStyle, classStyle: IVisageComponentStyle)
{
	const translateXRegex = /translateX\((.*?)\)/;
	const translateYRegex = /translateY\((.*?)\)/;
	const scaleRegex = /scale\((.*?)\)/;
	
	const updateState = (state: keyof(IVisageComponentStyle)) => 
	{
		// ensure component style structure
		if(!componentStyle[state]) componentStyle[state] = {};

		// the priority is thus: component state, class state, component normal, nothing
		const regexValue = (regex: RegExp) => 	componentStyle[state]![CssProperties.Transform]?.match(regex) || 
												classStyle[state]?.[CssProperties.Transform]?.match(regex) || 
												componentStyle.normal?.[CssProperties.Transform]?.match(regex) || 
												[""];
	
		// remove empty properties and extra spaces		
		componentStyle[state]![CssProperties.Transform] = [regexValue(translateXRegex)[0], regexValue(translateYRegex)[0], regexValue(scaleRegex)[0]].filter(v => !!v).join(" ");

		// if there is no transform porperty value after all, remove it from the component style so we don't have empty transform properties
		if(!componentStyle[state]![CssProperties.Transform]) delete componentStyle[state]![CssProperties.Transform];
	};

	Object.keys(componentStyle).forEach(state => updateState(state as keyof(IVisageComponentStyle)));
}

const specialCtaClassCase = new Set(["primary-cta", "secondary-cta", "primary-icon-cta", "secondary-icon-cta"]);

/**
 * **WARNING!! this function modifies the project data istself and is only intended for use here!! extracted only for unit testing and readability!! WARNING!!**
 *
 * @param {IGrapesJSProjectData} grapesProject
 * @param {Map<string, IVisageComponentStyle>} styleMap
 */
function mergeClassStyleIntoComponents(grapesProject: IGrapesJSProjectData, styleMap: Map<string, IVisageComponentStyle>)
{
	traverseGrapesProject(grapesProject, ({component}) =>
	{
		// ignore components with no classes - they either have a defined style or not, but we don't care about that here
		if(!component.classes?.length) return;

		// create missing map entries if needed
		const idSelector = `#${component.attributes.id}`;
		if(!styleMap.has(idSelector))
		{
			styleMap.set(idSelector, { normal: {}, hover: {}, active: {} });
		}
		
		// should exist in the map at this point
		const componentStyle = styleMap.get(idSelector)!;
		
		// merge the class styles with the component's styles
		for(const componentClass of component.classes)
		{
			const classStyle = styleMap.get(componentClass);
			if(!classStyle) continue;

			// special case for certain classes that grapes for some reason did not apply the hover state to the template despite it working in editor :|
			if(specialCtaClassCase.has(componentClass))
			{
				if(!classStyle.hover) classStyle.hover = {};
				classStyle.hover[CssProperties.Transform] = classStyle.hover[CssProperties.Transform] || "scale(1.1, 1.1)";
			}

			// the transform property is a special case. we need to combine the transform values from the class with the component
			mergeCssTransformProperty(componentStyle, classStyle);

			// now we merge; the component overwrites the class (remember we dealt with transform above so the component should always write over those)
			componentStyle.normal = { ...classStyle.normal, ...componentStyle.normal };
			componentStyle.hover = { ...classStyle.hover, ...componentStyle.hover };
			componentStyle.active = { ...classStyle.active, ...componentStyle.active };
		}

		// remove the classes from the component; we don't need nor want them at this point
		delete component.classes;
	});
}

/**
 * **WARNING!! this function modifies the project data istself and is only intended for use here!! extracted only for unit testing and readability!! WARNING!!**
 *
 * @param {IGrapesJSProjectData} grapesProject
 * @param {Map<string, IVisageComponentStyle>} stylesMap
 */
function populateComponentStylesIntoProject(grapesProject: IGrapesJSProjectData, styleMap: Map<string, IVisageComponentStyle>)
{
	for(const [selector, styles] of styleMap)
	{
		// we only need to populate styles for components here as we want to intentionally remove class styles from the project
		if(!selector.includes("#"))
		{
			continue;
		}
	
		if(styles.normal && Object.keys(styles.normal).length)
		{
			grapesProject.styles.push({
				selectors: [selector],
				style: { ...styles.normal },
			});
		}
	
		if(styles.hover && Object.keys(styles.hover).length)
		{
			grapesProject.styles.push({
				selectors: [selector],
				style: { ...styles.hover },
				state: CssStates.Hover,
			});
		}
	
		if(styles.active && Object.keys(styles.active).length)
		{
			grapesProject.styles.push({
				selectors: [selector],
				style: { ...styles.active },
				state: CssStates.Active,
			});
		}
	}
}

/**
 * Some components were defined with css classes in order to support CSS states like hover or click  
 * However, this causes other issues down the line, so we'd like to replace the classes with their explicit styles  
 * Essentially, we traverse the components and for each one we set new styles based on the normal state + class modifiers  
 * The `transform` property is a special case and needs to be combined from the normal state  
 * The reason for that is the `transform` property is used with both position and scale, unlike other css properties which are unique
 * 
 * **Important:** This function modifies the project data in-place, so make sure to pass in a writable object!
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 */
export function replaceCssClassesWithExplicitStyles(grapesProject: IGrapesJSProjectData)
{
	if(!grapesProject?.styles?.length) return;

	// build a map of class names to their respective styles for faster lookup
	// **WARNING!! this function modifies the project data istself and is only intended for use here!! extracted only for unit testing and readability!! WARNING!!**
	const styleMap = extractProjectStylesIntoMap(grapesProject);

	// replace component styles with the explicit styles
	// **WARNING!! this function modifies the project data istself and is only intended for use here!! extracted only for unit testing and readability!! WARNING!!**
	mergeClassStyleIntoComponents(grapesProject, styleMap);

	// add the styles back to the project - at this point we discard all classes and just add the component's styles
	// **WARNING!! this function modifies the project data istself and is only intended for use here!! extracted only for unit testing and readability!! WARNING!!**
	populateComponentStylesIntoProject(grapesProject, styleMap);
}

/**
 * Ensures that the given grapes project has a `voiceover` attribute on the root component of each frame
 * The voiceover attribute is used to display the property field in the UI editor and is the only place to edit the step voice instructions
 * Therefore, it is crucial that each project has this attribute set on the root component
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 */
export function ensureVoiceOverAttribute(grapesProject: IGrapesJSProjectData)
{
	if(!grapesProject?.pages) return;

	for(const page of grapesProject.pages)
	{
		if(!page) continue;

		for(const frame of page.frames)
		{
			const rootComponent = frame?.component?.components?.at(0);
			if(!rootComponent) continue;

			rootComponent.attributes[GjsAttributeNames.VoiceOver] = "";
		}
	}
}

/**
 * This function removes unwated css properties from specific component types in the given project
 * 
 * **Important:** This function modifies the project data in-place, so make sure to pass in a writable object!
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 * @param {Array<{ type: GjsComponentTypes, properties: Array<CssProperties>}>} propertiesToRemove
 */
export function removeCssProperties(grapesProject: IGrapesJSProjectData, propertiesToRemove: Array<{ type: GjsComponentTypes, properties: Array<CssProperties>}>)
{
	traverseGrapesProject(grapesProject, ({component}) =>
	{
		const componentType = component.type as GjsComponentTypes;
		const properties = propertiesToRemove.find(p => p.type === componentType)?.properties;
		if(!properties) return;

		for(const style of grapesProject.styles)
		{
			if(style.selectors.join("").includes(component.attributes.id))
			{
				for(const prop of properties)
				{
					delete style.style[prop];
				}
			}
		}
	});
}

/**
 * Creates a map of component ids to their associated styles at each css state
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 * @param {string[]} selectedComponentIds
 * @return {VisageContextStylesMap} 
 */
export function generateGrapesContextStylesMaps(grapesProject: IGrapesJSProjectData, selectedComponentIds: string[])
{
	const result: VisageContextStylesMap = {};

	for(const componentId of selectedComponentIds)
	{
		for (const state of Object.values(CssStates))
		{
			const style = getGrapesComponentStyleFromRedux(componentId, grapesProject, state) || {};
			if(!result[componentId]) result[componentId] = {} as any;
			result[componentId][state] = style;
		}
	}

	return result;
}

/**
 * Sets the editor context data in redux for the visage inspector
 *
 * @export
 * @param {string} projectId
 * @param {IGrapesJSProjectData} grapesProject
 * @param {string[]} selectedComponentIds
 */
export function updateGrapesEditorContext(projectId: string, grapesProject: IGrapesJSProjectData, selectedComponentIds: string[])
{
	const selectedComponents = new Array<IGrapesJSComponent>();
	selectedComponentIds.forEach(cId => 
	{
		const component = getGrapesComponentFromRedux(cId, grapesProject);
		if(component) selectedComponents.push(component);
	});

	store.dispatch(StoreActions.visage.setEditorContext({
		projectId: projectId,
		projectData: JsonCopy(grapesProject),
		components: selectedComponents,
		componentStyles: generateGrapesContextStylesMaps(grapesProject, selectedComponentIds),
	}));
}

/**
 * Ensures that cameras have the default action set beucase otherwise they won't be exported correctly
 * 
 * **Important:** This function modifies the project data in-place, so make sure to pass in a writable object!
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 */
export function ensureCameraAction(grapesProject: IGrapesJSProjectData)
{
	traverseGrapesProject(grapesProject, ({ component }) => 
	{
		if(component.type === GjsComponentTypes.Camera)
		{
			component.attributes[GjsAttributeNames.Action] = GjsActionTypes.EnlargeCamera;
		}
	});
}

/**
 * Removes the styling of e.g. the body node from the given grapesjs project  
 * the user should never be able to interact with them and the editor injects the styling on project load
 * 
 * **Important:** This function modifies the project data in-place, so make sure to pass in a writable object!
 *
 * @export
 * @param {IGrapesJSProjectData} grapesProject
 */
export function removeSpecialNodeStyling(grapesProject: IGrapesJSProjectData)
{
	if(!grapesProject.styles) return;

	const specialNodesToRemove = new Set([
		"body",		
	]);

	grapesProject.styles = grapesProject.styles.filter(style => !style.selectorsAdd || !specialNodesToRemove.has(style.selectorsAdd));
}

/**
 * Deletes the given components from the project data
 *
 * @export
 * @param {string[]} componentIds
 * @param {string} [grapesProjectId]
 * @return {*} 
 */
export function deleteVisageComponents(componentIds: string[], grapesProjectId?: string): void
{
	grapesProjectId = grapesProjectId || store.getState().visageModule.editorContext.projectId;
	const projectData = JsonCopy(store.getState().visageModule.stepUIData[grapesProjectId] ?? store.getState().visageModule.uiTemplateData[grapesProjectId]?.content ?? {});

	if(!componentIds?.length || !projectData.pages) return;

	for(const componentId of componentIds)
	{
		// the ^ sign moves to the parent of the matched path, so if we want to get the actual parent component we go up 3 times:
		// once to the results array, once more to the 'components' attribute, and once more to the actual parent component
		const query = `$..components[?(@.attributes?.id === "${componentId}")]^^^`;
		const parent = JSONPath({ path: query, json: projectData })?.[0] as IGrapesJSComponent;
		if(parent?.components)
		{
			for(let i = parent.components.length - 1; i >= 0; i--)
			{
				const child = parent.components[i];
				if(child.attributes[GjsAttributeNames.NotDeletable] !== undefined) continue;

				if(child.attributes.id === componentId)
				{
					parent.components.splice(i, 1);
				}
			}
		}
	}

	new SetGrapesProjectAction({ grapesProjectId: grapesProjectId, newData: projectData, actionName: `Deleted ${componentIds.length} Components` });
}

const gjsTypeToVisageTypeMap = {
	[GjsComponentTypes.Div]: VisageComponentTypes.Container,
	[GjsComponentTypes.Text]: VisageComponentTypes.Text,
	[GjsComponentTypes.Image]: VisageComponentTypes.Image,
	[GjsComponentTypes.Audio]: VisageComponentTypes.Audio,
	[GjsComponentTypes.Video]: VisageComponentTypes.Video,
	[GjsComponentTypes.Camera]: VisageComponentTypes.Camera,
};

/**
 * Converts from a grapesjs component definition to a visage component definition
 * 
 * Children are converted recursively
 * 
 * A component's style (for each state) is saved in the `style` property
 *
 * @export
 * @param {IGrapesJSComponent} component the grapesjs component to convert
 * @param {IGrapesJSProjectData} grapesProject the project data that the component belongs to
 * @return {*}  {IVisageComponentDefinition} the converted visage component
 */
export function convertGjsComponentToVisageComponent(component: IGrapesJSComponent, grapesProject: IGrapesJSProjectData): IVisageComponentDefinition
{
	const {
		// we filter the id and action attributes because id will be generated, and action will be stored in the actions array
		id, 
		[GjsAttributeNames.Action]: action,
		// we filter out events because they'll be added by grapesjs later
		onmousedown, onclick, ondblclick, oncontextmenu, onmouseenter, onmouseleave, onmouseover, onmouseout, onmousemove, onmouseup, onkeydown, onkeypress, onkeyup,
		// the rest we keep
		...filteredAttributes } = component.attributes;

	const output: IVisageComponentDefinition = {
		displayName: component.name || component["custom-name"] || component.type,
		type: gjsTypeToVisageTypeMap[component.type] || VisageComponentTypes.Container,
		actions: [component.attributes[GjsAttributeNames.Action]].filter(a => Boolean(a)),
		styleClassList: component.classes || [],
		textContent: component.content || "",
		htmlAttributes: filteredAttributes,
		style: {
			normal: getGrapesComponentStyleFromRedux(component.attributes.id, grapesProject, CssStates.Normal),
			hover: getGrapesComponentStyleFromRedux(component.attributes.id, grapesProject, CssStates.Hover),
			active: getGrapesComponentStyleFromRedux(component.attributes.id, grapesProject, CssStates.Active),
		},
		children: component.components?.map(c => convertGjsComponentToVisageComponent(c, grapesProject)) || [],
	};

	return output;
}

/**
 * Copies the given components to the clipboard in visage component format, converting the given grapesjs components
 *
 * @export
 * @param {string[]} componentIds the ids of the grapesjs components to copy, defaults to the current selection
 * @param {string} [grapedProjectId] the id of the project to copy from, defaults to the current project
 */
export function copyVisageComponents(componentIds?: string[], grapedProjectId?: string): void
{
	componentIds = componentIds || store.getState().visageModule.selectedComponentIds;
	grapedProjectId = grapedProjectId || store.getState().visageModule.editorContext.projectId;
	const projectData = store.getState().visageModule.stepUIData[grapedProjectId] ?? store.getState().visageModule.uiTemplateData[grapedProjectId]?.content ?? {};

	const copiedComponents = new Array<IVisageComponentDefinition>();
	for(const id of componentIds)
	{
		const component = getGrapesComponentFromRedux(id, projectData);
		if(component)
		{
			copiedComponents.push(convertGjsComponentToVisageComponent(component, projectData));
		}
	}

	store.dispatch(StoreActions.visage.setClipboard(copiedComponents));
}

/**
 * For each selected component, we copy every visage component from the clipbaord and convert it into a new grapesjs component, 
 * which is then pushed as a child of the selected component (named "parent component")  
 * The component styles will be pushed into the project's styles array
 * 
 * If no parent components are given, the root component of the project will be used to paste into
 * 
 * @export
 * @param {string[]} [parentComponentIds] the ids of the components to paste into, defaults to the current selection, fallbacks to the root component
 * @param {string} [grapedProjectId] the id of the project to paste into, defaults to the current project
 * @return {*}  {string[]} a list of the ids of the pasted components (only the root component for each pasted hierarchy) or an empty array if nothing was pasted
 */
export function pasteVisageComponents(parentComponentIds?: string[], grapedProjectId?: string): string[]
{
	const outputIds = new Array<string>();

	grapedProjectId = grapedProjectId || store.getState().visageModule.editorContext.projectId;
	const projectData = JsonCopy(store.getState().visageModule.stepUIData[grapedProjectId] ?? store.getState().visageModule.uiTemplateData[grapedProjectId]?.content ?? {});
	
	const rootQuery = `$..components[0]`;
	const rootComponent = JSONPath({ path: rootQuery, json: projectData })?.[0] as IGrapesJSComponent;
	if(!rootComponent) return outputIds;

	const clipboard = store.getState().visageModule.clipboard;
	if(!clipboard.length) return outputIds;

	parentComponentIds = parentComponentIds || store.getState().visageModule.selectedComponentIds;
	for(const parentId of parentComponentIds)
	{
		const query = `$..components[?(@.attributes?.id === "${parentId}")]`;
		const parentComponent = (JSONPath({ path: query, json: projectData })?.[0]) as IGrapesJSComponent || rootComponent;
		console.log(parentComponent);

		for(const visageComponent of clipboard)
		{
			const styles = new Array<IGrapesJSStyle>();
			const newComponent = convertVisageComponentToGrapes(JsonCopy(visageComponent), styles);
			if(!newComponent) continue;

			outputIds.push(newComponent.attributes.id);

			if(!parentComponent.components) parentComponent.components = [];
			parentComponent.components.push(newComponent);

			projectData.styles.push(...styles);
		}
	}

	new SetGrapesProjectAction({ grapesProjectId: grapedProjectId, newData: projectData, actionName: `Pasted ${clipboard.length} Components` });

	return outputIds;
}

export function getComponentNames(stepId: string)
{
	const ui = store.getState().visageModule.stepUIData[stepId];
	if ( ! ui )
		throw new Error(`Invalid Step ID '${stepId}'`)

	const components = JSONPath({
		path: `$..components[?(@['custom-name'])]`,
		json: ui.pages
	}) as Array<{ 'custom-name': string, attributes: Record<string, any> }>;

	return components.map(component => ({
		name: component['custom-name'],
		isSmartComponent: component.attributes[GjsAttributeNames.SmartIdentifier] === SmartIdentifierStates.Enabled
	}));
}